import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ls from 'local-storage';

import { MODELS_WALL_QUICKFILTER_DEFAULT } from '@configs/WALLS_CONFIG';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useModelContext } from '@contexts/ModelContext';
import { useStateValue } from '@contexts/StateProvider';

import ModelsSelectorAccounting from '@components/ContactsSelector/ModelsSelector/ModelsSelectorAccounting';

import { ContactsSelectorProvider } from '../Common/ContactsSelectorContext';
import ModelsSelector from './ModelsSelector';

const ModelsSelectorWrapper = ({
    action, //action in URL: find similar models (?)
    addModelProps,
    currentEvent,
    currentFolderID, //folder di destinazione nello scope "folder"
    currentPackage,
    defaultView, //view to start with
    disabledModels,
    disabledModelAvailability,
    fixedView, //view can't be changed
    isDrawer,
    layouttype, // left || fullpage
    itemClickAction, // preview-drawer || navigate
    modelToCompare, //find similar models
    multipleSelection,
    packageGroup,
    projectId,
    scope,
    startOnProjectModels,
}) => {
    const [{ areaContext }] = useStateValue();

    const layoutContext = useLayoutContext();
    const { breakpoints } = layoutContext;
    const tableView = breakpoints < 4;

    const modelContextValues = useModelContext();
    const { listType: defaultListType, activeSet: defaultActiveSet } = modelContextValues;

    const lastUsedView = ls.get(`nw_config_${areaContext.area}_${scope.replace('-', '')}_view`);

    const isAccountingSearch = scope === 'accounting-search';

    const currentView = useMemo(() => {
        if (tableView) {
            return 'table';
        }
        if (fixedView) {
            return defaultView ? defaultView : 'cards';
        }
        if (lastUsedView) {
            return lastUsedView;
        }
        if (defaultView) {
            return defaultView;
        }
        return 'cards';
    }, [defaultView, fixedView, tableView, lastUsedView]);

    let defaultQuickFilters = MODELS_WALL_QUICKFILTER_DEFAULT;

    const [searchParams, setSearchParams] = useSearchParams();
    const clearQF = searchParams.get('clear_qf');

    const localStorageNameNoScope = `nw_config_${areaContext.area}`;
    const savedQuickFilters = ls.get(`${localStorageNameNoScope}_quickFilters`);
    if (savedQuickFilters) {
        defaultQuickFilters = savedQuickFilters;
    }

    const [selectorAction, setSelectorAction] = useState(action || null);
    const [selectorActionID, setSelectorActionID] = useState(modelToCompare || null);
    const [showOnlyModelsWithBooks, setShowOnlyModelsWithBooks] = useState(currentPackage ? true : false);
    const enableShowOnlyModelsWithBooks = currentPackage ? true : false;
    const enableModelAvailability = !disabledModelAvailability;
    const itemType = 'model';

    const data_context = useMemo(
        () => ({
            addModelProps,
            area: areaContext.area,
            currentEvent,
            currentFolderID,
            currentPackage,
            itemType,
            packageGroup,
            projectId,
            scope,
            selectorAction,
            selectorActionID,
        }),
        [
            addModelProps,
            areaContext.area,
            currentEvent,
            currentFolderID,
            currentPackage,
            itemType,
            packageGroup,
            projectId,
            scope,
            selectorAction,
            selectorActionID,
        ]
    );

    const data_selection = {
        quickFilters: defaultQuickFilters,
    };

    const data_actions = useMemo(
        () => ({
            itemClickAction,
            multipleSelection,
        }),
        [itemClickAction, multipleSelection]
    );

    const data_layout = useMemo(
        () => ({
            drawer: isDrawer,
            type: layouttype,
            leftMargin: null, //integer (px) for left margin
            view: {
                default: defaultView,
                current: currentView,
                fixed: fixedView ? true : false,
            },
        }),
        [currentView, defaultView, fixedView, isDrawer, layouttype]
    );

    const data_additional = useMemo(
        () => ({
            disabledItems: disabledModels ? disabledModels : [],
            enableModelAvailability,
            enableShowOnlyModelsWithBooks,
            showOnlyModelsWithBooks,
            startOnProjectModels,
        }),
        [
            disabledModels,
            enableModelAvailability,
            enableShowOnlyModelsWithBooks,
            showOnlyModelsWithBooks,
            startOnProjectModels,
        ]
    );

    const startListType = useMemo(() => {
        if (isAccountingSearch) {
            return 'smart-lists';
        }
        return defaultListType || 'all';
    }, [areaContext.area, defaultListType, isAccountingSearch]);

    const startActiveSet = useMemo(() => {
        if (isAccountingSearch) {
            return 'act';
        }
        return defaultActiveSet || 'all';
    }, [defaultActiveSet, isAccountingSearch]);

    return (
        <ContactsSelectorProvider
            dataContext={data_context}
            dataSelection={data_selection}
            dataActions={data_actions}
            dataLayout={data_layout}
            dataAdditional={data_additional}
            startListType={startListType}
            startActiveSet={startActiveSet}
            clearQF={clearQF}
        >
            {isAccountingSearch ? <ModelsSelectorAccounting /> : <ModelsSelector />}
        </ContactsSelectorProvider>
    );
};

export default ModelsSelectorWrapper;
